import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { buildClassName } from '@bluebeam/build-class-name';
import { useDataAttributes } from '@bluebeam/react-hooks';
import { useTranslator } from '@bluebeam/react-i18n';
import { GeneralIcons } from '@bluebeam/react-skyline-art';
import { motion } from 'framer-motion';
import { Children, cloneElement, useEffect, useRef } from 'react';
import GrowlerAction from './GrowlerAction';
import { reactNodeToString } from './reactNodeToString';
import { useGrowlerManager } from './useGrowlerManager';
const MAX_DISMISS_MS = 10_000;
const MIN_DISMISS_MS = 4_000;
const WORDS_PER_MINUTE = 120;
const WORDS_PER_SECOND = WORDS_PER_MINUTE / 60;
/** A growler is a type of **molecule** that represents a toast-like alert. */
const Growler = ({ ariaClose, ariaLabel, showCloseButton, className, children, description, forceAutoDismiss, onDismiss, variant, ...rest }) => {
    const growlerRef = useRef(null);
    const { dismissGrowler } = useGrowlerManager();
    const childCount = Children.count(children);
    const isChildrenVisible = childCount === 1 || childCount === 2;
    const shouldAutoDismiss = (!isChildrenVisible && !showCloseButton) || forceAutoDismiss;
    const dataAttributes = useDataAttributes(rest);
    const { translate } = useTranslator(['local']);
    useEffect(() => {
        if (shouldAutoDismiss) {
            return undefined;
        }
        const activeElement = document.activeElement;
        if (growlerRef.current) {
            growlerRef.current.focus();
        }
        if (!activeElement) {
            return undefined;
        }
        return () => {
            activeElement.focus();
        };
    }, [shouldAutoDismiss]);
    useEffect(() => {
        if (!shouldAutoDismiss) {
            return undefined;
        }
        const estimatedTimeToRead = Math.ceil(reactNodeToString(description).trim().split(/\s+/).length / WORDS_PER_SECOND) *
            1000;
        const dismissTimeout = Math.min(Math.max(MIN_DISMISS_MS, estimatedTimeToRead), MAX_DISMISS_MS);
        const dismissTimeoutId = setTimeout(dismissGrowler, dismissTimeout);
        return () => {
            clearTimeout(dismissTimeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dismissGrowler, shouldAutoDismiss]);
    const renderIcon = () => {
        if (variant === 'error') {
            return (_jsx(GeneralIcons.ErrorIcon, { className: "shrink-0 grow-0 basis-auto text-white", size: "32px" }));
        }
        if (variant === 'success') {
            return (_jsx(GeneralIcons.CheckCircledIcon, { className: "text-alerts-success-green shrink-0 grow-0 basis-auto", size: "32px" }));
        }
        return null;
    };
    return (_jsx(motion.div, { animate: "visible", "aria-describedby": "growler-description", className: buildClassName('elevation-3 items-center rounded-lg bg-black text-white', 'left-0 right-0 mx-auto flex min-w-[304px] max-w-[680px] px-4 py-3', 'focus-visible:outline-primary-blue fixed top-[68px] w-fit focus:outline-none focus-visible:outline-2', className), exit: "unmount", initial: "mount", ref: growlerRef, role: "alert", tabIndex: -1, variants: {
            mount: {
                opacity: 0,
                y: '-265px',
            },
            unmount: {
                opacity: 0,
            },
            visible: {
                opacity: 1,
                y: '0',
            },
        }, ...dataAttributes, children: _jsxs("div", { className: "flex min-h-[32px] shrink grow basis-auto items-center gap-4", children: [renderIcon(), _jsx("div", { "aria-label": ariaLabel, className: "skyline-text-body-1 my-0 mr-0 grow basis-auto p-0 leading-5 text-white", id: "growler-description", children: description }), isChildrenVisible &&
                    Children.map(children, (child) => {
                        return cloneElement(child, {
                            onClick: () => {
                                if (child.props.onClick) {
                                    child.props.onClick();
                                }
                                dismissGrowler();
                            },
                        });
                    }), showCloseButton && (_jsx("button", { "aria-label": ariaClose ?? translate('local:dismiss'), className: "focus-visible:outline-primary-blue h-6 cursor-pointer rounded-lg border-none bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2", onClick: () => {
                        if (onDismiss)
                            onDismiss();
                        dismissGrowler();
                    }, type: "button", children: _jsx(GeneralIcons.CloseIcon, { size: "24px", className: "text-white" }) }))] }) }));
};
Growler.Action = GrowlerAction;
export default Growler;
//# sourceMappingURL=Growler.js.map